import {
  useApproverGroups,
  useChangeFunctionApproverGroupApprovalRequirements,
  useEditApproverGroups,
  useEditChangeFunctionApproverGroupApprovalRequirements,
  ApproverGroup,
  ApproverGroupFormValues,
  ApproverGroupPayload,
  ChangeFunctionApproverGroupApprovalRequirement
} from '@domain/shifthandover'
import {UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {isEqual} from 'lodash'
import {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react'
import {Control, useForm, UseFormHandleSubmit} from 'react-hook-form'

type ApproversAdjustFormDataState = {
  matrixData: ChangeFunctionApproverGroupApprovalRequirement[]
  setMatrixData: Dispatch<SetStateAction<ChangeFunctionApproverGroupApprovalRequirement[]>>
  isSavingMatrix: boolean
  isSavingGroups: boolean
  currentValues: ApproverGroup[]
  control: Control<ApproverGroupFormValues>
  handleSubmit: UseFormHandleSubmit<ApproverGroupFormValues>
  onSubmit: () => void
  approverGroupsQuery: UseQueryResult<ApproverGroup[], AxiosError>
  changeFunctionApproverGroupApprovalRequirementsQuery: UseQueryResult<
    ChangeFunctionApproverGroupApprovalRequirement[],
    AxiosError
  >
}

const generateFromGroups = (data?: ApproverGroup[]) => ({
  groups: (data || []).map((group) => ({
    ...group,
    approvers: group.approvers || []
  }))
})

type Params = {
  plantId: string
  handleClose: (data?: {isSuccess: boolean}) => void
}

export const useApproversAdjustFormData = ({
  plantId,
  handleClose
}: Params): ApproversAdjustFormDataState => {
  const [matrixData, setMatrixData] = useState<ChangeFunctionApproverGroupApprovalRequirement[]>([])

  const onSuccess = useCallback(() => handleClose({isSuccess: true}), [handleClose])

  const changeFunctionApproverGroupApprovalRequirementsQuery =
    useChangeFunctionApproverGroupApprovalRequirements({plantId})
  useEffect(() => {
    if (changeFunctionApproverGroupApprovalRequirementsQuery.data)
      setMatrixData(changeFunctionApproverGroupApprovalRequirementsQuery.data)
  }, [changeFunctionApproverGroupApprovalRequirementsQuery.data])
  const {
    mutateAsync: saveChangeFunctionApproverGroupApprovalRequirements,
    isPending: isSavingMatrix
  } = useEditChangeFunctionApproverGroupApprovalRequirements({plantId})
  const {mutateAsync: saveGroups, isPending: isSavingGroups} = useEditApproverGroups({plantId})
  const approverGroupsQuery = useApproverGroups({plantId})

  const {control, handleSubmit, reset, watch} = useForm<ApproverGroupFormValues>({
    defaultValues: generateFromGroups(approverGroupsQuery.data),
    mode: 'onChange'
  })
  useEffect(() => {
    reset(generateFromGroups(approverGroupsQuery.data))
  }, [approverGroupsQuery.data, reset])
  const currentValues = watch('groups')

  const onSubmit = () => {
    const areGroupsSame = isEqual(currentValues, approverGroupsQuery.data)
    const areMatricesSame = isEqual(
      changeFunctionApproverGroupApprovalRequirementsQuery.data,
      matrixData
    )

    if (areGroupsSame && areMatricesSame) {
      handleClose()
      return
    }

    const payload = currentValues.reduce<ApproverGroupPayload>((acc, group) => {
      acc[group.id] = (group.approvers || []).map(({id}) => id)
      return acc
    }, {})

    if (areGroupsSame && !areMatricesSame) {
      void saveChangeFunctionApproverGroupApprovalRequirements(matrixData, {onSuccess})
      return
    }
    if (!areGroupsSame && areMatricesSame) {
      void saveGroups(payload, {onSuccess})
      return
    }
    void Promise.all([
      saveGroups(payload),
      saveChangeFunctionApproverGroupApprovalRequirements(matrixData)
    ]).then(onSuccess)
  }

  return {
    isSavingGroups,
    isSavingMatrix,
    approverGroupsQuery,
    changeFunctionApproverGroupApprovalRequirementsQuery,
    control,
    handleSubmit,
    onSubmit,
    currentValues,
    matrixData,
    setMatrixData
  }
}
